import {Platform, Pressable, View} from 'react-native'

import {atoms as a, useTheme} from '#/alf'
import {useInteractionState} from '#/components/hooks/useInteractionState'
import {BulletList_Stroke2_Corner0_Rounded as Playlist} from '#/components/icons/BulletList'
import {Text} from '#/components/Typography'
import {MODULE_ASPECT} from './types'

/**
 * The way into a module, as a cell of a capsule grid.
 *
 * The third of the three add-cards, and identical to the other two on purpose (see
 * `AddFlashbookCard` for the argument): a reader who has learnt what the dashed
 * accent card means in one grid has learnt it in all of them, and a way in drawn
 * differently in each would teach that the grids are different kinds of thing rather
 * than three kinds of work.
 *
 * Two decisions of its own:
 *
 *  - **It is the *book's* proportions**, like the module card it sits beside. The
 *    Capsules tab draws both kinds at once, and a dashed cell standing a head taller
 *    than the module cards it is offering to create would be the tallest thing in a
 *    grid of playlists.
 *  - **Its mark is a list.** A module is a playlist — an ordered row of capsules —
 *    and the app already has that icon. Nothing new is drawn for it, which is also
 *    why this card carries no artwork of its own to keep in step with the feature.
 *
 * What it opens is the caller's: the profile opens the module builder, and a
 * classroom opens it attached to the room, which is the one difference between the
 * two ways in.
 */
export function AddModuleCard({
  onPress,
  testID,
  label,
  caption,
  aspectRatio = MODULE_ASPECT,
}: {
  onPress: () => void
  /** The recipe that drives this grid names its own cells. */
  testID: string
  /** What the card says on itself: the act, in the caller's words. */
  label: string
  /** The line under it, in the place a module keeps its title. */
  caption: string
  aspectRatio?: number
}) {
  const t = useTheme()
  const {
    state: pressed,
    onIn: onPressIn,
    onOut: onPressOut,
  } = useInteractionState()

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={caption}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={[a.flex_col, Platform.OS === 'web' ? {cursor: 'pointer'} : null]}>
      <View
        style={[
          a.justify_center,
          a.align_center,
          a.rounded_md,
          a.gap_sm,
          a.p_md,
          {
            aspectRatio,
            borderWidth: 1.5,
            borderStyle: 'dashed',
            borderColor: t.palette.primary_500,
            /** See `AddCapsuleCard`: filled faintly, deepened when pressed. */
            backgroundColor: pressed
              ? t.atoms.bg_contrast_50.backgroundColor
              : t.atoms.bg_contrast_25.backgroundColor,
          },
          Platform.OS === 'web' && a.w_full,
        ]}>
        <Playlist size="4xl" fill={t.palette.primary_500} />

        <Text
          style={[
            a.text_sm,
            a.font_semi_bold,
            a.text_center,
            {color: t.palette.primary_500},
          ]}>
          {label}
        </Text>
      </View>

      <View style={[a.pr_xs, {paddingTop: 6, gap: 4}]}>
        <Text
          style={[a.text_sm, a.leading_tight, t.atoms.text_contrast_medium]}
          numberOfLines={2}>
          {caption}
        </Text>
      </View>
    </Pressable>
  )
}
